<template>
  <div class="bpmn-wrapper">
    <!-- Overlay Controls -->
    <div class="replay-overlay">
      <!-- PLAY / PAUSE -->
      <v-btn
        color="primary"
        fab
        dark
        @click="onReplayClick"
      >
        <v-icon>
          {{ isReplaying && !isPaused ? 'mdi-pause' : 'mdi-play' }}
        </v-icon>
      </v-btn>

      <!-- RESET -->
      <v-btn
        v-if="isReplaying"
        color="red"
        fab
        dark
        @click="resetReplay"
      >
        <v-icon>mdi-restart</v-icon>
      </v-btn>
    </div>

    <div class="bpmn-container">
      <div ref="canvas" class="canvas"></div>
      <div ref="propertiesPanel" class="properties-panel-parent"></div>
    </div>
  </div>
</template>

<script>
// import BpmnJS from 'bpmn-js/lib/NavigatedViewer'
// import TokenSimulationModule from 'bpmn-js-token-simulation/lib/viewer'
import BpmnJS from 'bpmn-js/lib/Modeler'
import TokenSimulationModule from 'bpmn-js-token-simulation'
import SimulationSupportModule from 'bpmn-js-token-simulation/lib/simulation-support'
import {
  BpmnPropertiesPanelModule,
  BpmnPropertiesProviderModule,
  CamundaPlatformPropertiesProviderModule
} from 'bpmn-js-properties-panel'
import 'bpmn-js/dist/assets/diagram-js.css'
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css'

import CamundaBpmnModdle from 'camunda-bpmn-moddle/resources/camunda.json'
import 'bpmn-js-token-simulation/assets/css/bpmn-js-token-simulation.css'
import '@bpmn-io/properties-panel/dist/assets/properties-panel.css'

export default {
  props: {
    xml: {
      type: String,
      required: true
    },
    activities: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  data() {
    return {
      viewer: null,
      activeSequenceFlow: new Map(),

      isReplaying: false,
      isPaused: false
    }
  },

  watch: {
    xml() {
      this.loadDiagram()
    }
  },

  async mounted() {
    this.viewer = new BpmnJS({
      container: this.$refs.canvas,

      moddleExtensions: {
        camunda: CamundaBpmnModdle
      },

      additionalModules: [
        BpmnPropertiesPanelModule,
        BpmnPropertiesProviderModule,
        CamundaPlatformPropertiesProviderModule,
        TokenSimulationModule,
        SimulationSupportModule
      ],
      propertiesPanel: {
        parent: this.$refs.propertiesPanel
      }
    })

    await this.loadDiagram()
  },

  methods: {
    async loadDiagram() {
      if (!this.xml) return

      const result = await this.viewer.importXML(this.xml)

      /* if (result.warnings?.length) {
        console.warn(result.warnings)
      } */

      const canvas = this.viewer.get('canvas')
      const elementRegistry = this.viewer.get('elementRegistry')
      const startEvent = elementRegistry.find(el =>
        el.type === 'bpmn:StartEvent'
      )
      if (startEvent) {
        canvas.zoom(1.2, { x: startEvent.x, y: startEvent.y })
      } else {
        canvas.zoom('fit-viewport')
      }

      var simulation = this.viewer.get('simulationSupport')
      simulation.toggleSimulation(true)
    },

    async onReplayClick() {
      if (!this.isReplaying) {
        this.startReplay()
      } else if (this.isPaused) {
        this.resumeReplay()
      } else {
        this.pauseReplay()
      }
    },
    async startReplay() {
      if (this.isReplaying) return

      const simulation = this.viewer.get('simulationSupport')
      simulation.toggleSimulation(false)
      simulation.toggleSimulation(true)

      this.activeSequenceFlow.clear()

      this.isReplaying = true
      this.isPaused = false

      await this.replay(this.activities)

      this.isReplaying = false
    },
    pauseReplay() {
      const pauseSim = this.viewer.get('pauseSimulation')
      pauseSim.pause()
      this.isPaused = true
    },
    resumeReplay() {
      const pauseSim = this.viewer.get('pauseSimulation')
      pauseSim.unpause()
      this.isPaused = false
    },
    resetReplay() {
      const simulation = this.viewer.get('simulationSupport')

      this.isPaused = false
      this.isReplaying = false
      this.activeSequenceFlow.clear()

      simulation.toggleSimulation(false)
      simulation.toggleSimulation(true)
    },
    async replay(activities) {
      if (!activities?.length) return

      const simulation = this.viewer.get('simulationSupport')
      const elementRegistry = this.viewer.get('elementRegistry')

      this.isReplaying = true

      const simulationData = activities.map(a => a.activityId)

      for (let i = 0; i < simulationData.length; i++) {
        const step = simulationData[i]
        const nextStep = simulationData[i + 1]
        const previousStep = simulationData[i - 1]

        const element = elementRegistry.get(step)
        if (!element) continue

        const type = element.businessObject.$type

        if (type.includes('Gateway')) {
          await this.handleFlowGateway(simulation, step, nextStep, elementRegistry)
        }

        if (type.includes('Event')) {
          await this.handleFlowEvent(simulation, step, previousStep, elementRegistry)
        }

        if (type.includes('Task') || type.includes('Activity')) {
          await this.handleFlowActivity(simulation, step, nextStep)
        }
      }
    },
    async handleFlowGateway(simulation, step, nextStep, elementRegistry) {
      const gatewayElement = elementRegistry.get(step)

      const outgoing = gatewayElement.outgoing || []

      // if only one outgoing flow
      if (outgoing.length <= 1) {
        await simulation.elementEnter(step)
        return
      }

      if (!this.activeSequenceFlow.has(step)) {
        const defaultFlow =
            gatewayElement.businessObject.default?.targetRef?.id

        this.activeSequenceFlow.set(step, defaultFlow)
      }

      if (this.activeSequenceFlow.get(step) !== nextStep) {
        const targetFlow =
            gatewayElement.businessObject.outgoing.find(
              f => f.targetRef.id === nextStep
            )

        if (targetFlow) {
          this.activeSequenceFlow.set(step, targetFlow.targetRef.id)
          simulation.triggerElement(step)
        }
      }

      await simulation.elementEnter(step)
    },
    async handleFlowEvent(simulation, step, previousStep, elementRegistry) {
      const el = elementRegistry.get(step)
      const type = el?.businessObject?.$type

      if (type === 'bpmn:EndEvent') {
      } else {
        simulation.triggerElement(step)
      }

      if (previousStep) {
        simulation.triggerElement(previousStep)
      }
    },
    async handleFlowActivity(simulation, step, nextStep) {
      if (nextStep?.includes('Event')) {
        simulation.triggerElement(step)
      }

      await simulation.elementEnter(step)
    }
  }
}
</script>

<style>
.bpmn-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 600px; /* match canvas height */
}

.canvas {
  flex: 1;               /* take remaining space */
  border: 1px solid #ccc;

  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;

  cursor: grab;
}
.canvas * {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
}
.canvas:active {
  cursor: grabbing;
}
/* disable properties panel input, make it view only */
.bio-properties-panel-checkbox,
.properties-panel-parent input,
.properties-panel-parent textarea,
.properties-panel-parent select,
.properties-panel-parent button {
  pointer-events: none;
}

.bts-toggle-mode, .bts-palette, .bts-set-animation-speed {
  visibility: hidden;
}

.djs-overlays[data-container-id="Event_start"] {
  display: none !important;
}

.bpmn-wrapper {
  position: relative;
  width: 100%;
}

.replay-overlay {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 20;

  display: flex;
  gap: 10px;
}
</style>
